import { apiFetch } from '@ami/react/core';
import _ from 'lodash';
import { ProductGroupVisibility } from '../products/productGroup';
import { ProductVisibility } from '../products/product';
import { UnitOfMeasure } from '../coupler-styles/unitOfMeasureTypes';

export class QuoteCouplerStyle {
  constructor(json) {
    if (json) {
      this.id = json.id;
      this.name = json.name;
      this.description = json.description;
      this.attachmentPointId = json.attachmentPointId;
      this.weightCategoryId = json.weightCategoryId;
      this.tipRadius = json.tipRadius;
      this.tipRadiusUnits = json.tipRadiusUnits;
      this.type = json.type;
      this.availableDecisionTypes = json.availableDecisionTypes;
      this.availableOilReadyTypes = json.availableOilReadyTypes;
      this.brands = json.brands;
      this.style = json.style;
      this.preferredMakes = json.preferredMakes;
      return;
    }
    this.id = null;
    this.name = '';
    this.description = '';
    this.weightCategoryId = '';
    this.tipRadius = null;
    this.tipRadiusUnits = UnitOfMeasure.inches.id;
    this.attachmentPointId = null;
    this.type = null;
    this.style = 'Other';
    this.preferredMakes = [];
    this.availableDecisionTypes = [];
    this.availableOilReadyTypes = [];
  }
}

export class QuoteProductConfiguration {
  constructor(json) {
    this.attachmentPoints = json.attachmentPoints;
    this.productCategories = json.productCategories;
    this.productGroups = json.productGroups;
    this.products = json.products;
    this.isoCurrencySymbol = json.isoCurrencySymbol;
    this.taxRate = json.taxRate;
    this.discountPercentage = json.discountPercentage;
    this.couplerStyles = this._parseCouplerStyles(json.couplerStyles);
    this.preferredBrandId = json.preferredBrandId;
    this.preferredSeriesId = json.preferredSeriesId;
    this.inventoryItems = json.inventoryItems;
  }

  static async getProductConfigurationFromQuote(quote) {
    return await this.getProductConfiguration(
      quote.step1,
      quote.attachmentPoints
    );
  }

  static async getProductConfiguration(step1Data, quoteAttachmentPoints = []) {
    const payload = {
      dealerBranchId: step1Data.dealerBranchId,
      carrierId: step1Data.carrierId,
      weightCategoryId: step1Data.weightCategoryId,
      machineId: step1Data.machineId,
      priceZoneId: step1Data.priceZoneId,
      quoteAttachmentPoints,
    };

    const result = await apiFetch(
      'quotes/configuration/products',
      'POST',
      payload
    );
    return new QuoteProductConfiguration(result);
  }

  getProductCategoriesByAttachmentPointId = (
    attachmentPointId,
    isDealerApp
  ) => {
    const selectedAttachmentPoint = this.attachmentPoints.find(
      (x) => x.id === attachmentPointId
    );
    if (selectedAttachmentPoint == null) return [];
    return this.productCategories
      .filter((c) => selectedAttachmentPoint.productCategoryIds.includes(c.id))
      .filter((c) =>
        this.productGroups.some(
          (pg) =>
            pg.productCategoryIds.includes(c.id) &&
            !_.isEmpty(
              this.getProductGroupsByProductCategoryId(
                c.id,
                isDealerApp,
                undefined
              )
            )
        )
      );
  };

  getProductGroupsByProductCategoryId(
    categoryId,
    isDealerApp,
    productGroupIdToForceInclude
  ) {
    const productGroupsForProductCategory = this.productGroups.filter((pg) =>
      pg.productCategoryIds.includes(categoryId)
    );
    const visibleProductGroups = productGroupsForProductCategory.filter(
      (pg) => {
        var dealerVisible =
          isDealerApp &&
          (ProductGroupVisibility[pg.visibility] ===
            ProductGroupVisibility.VisiblePublic ||
            ProductGroupVisibility[pg.visibility] ===
              ProductGroupVisibility.VisibleInternalRestrictedPublic);
        var amiVisible =
          !isDealerApp &&
          ProductGroupVisibility[pg.visibility] !==
            ProductGroupVisibility.Hidden;
        var forceVisible = pg.id === productGroupIdToForceInclude;
        var productGroupVisible = dealerVisible || amiVisible || forceVisible;
        var anyProductsVisible = !_.isEmpty(
          this.getProductsByProductGroupId(pg.id, isDealerApp, undefined)
        );
        return productGroupVisible && anyProductsVisible;
      }
    );
    return visibleProductGroups;
  }

  getProductsByProductGroupId(
    productGroupId,
    isDealerApp,
    productIdToForceInclude
  ) {
    const productsForProductGroup = this.products.filter(
      (p) => p.productGroupId === productGroupId
    );
    const visibleProducts = productsForProductGroup.filter((p) => {
      var dealerVisible =
        isDealerApp &&
        (ProductVisibility[p.visibility] === ProductVisibility.VisiblePublic ||
          ProductVisibility[p.visibility] ===
            ProductVisibility.VisibleInternalRestrictedPublic);
      var amiVisible =
        !isDealerApp &&
        ProductVisibility[p.visibility] !== ProductVisibility.Hidden;
      var forceVisible = p.id === productIdToForceInclude;
      return dealerVisible || amiVisible || forceVisible;
    });
    return visibleProducts;
  }

  getProductsByFilter(
    productGroupId,
    isDealerApp,
    productIdToForceInclude,
    thumbBucketProfileId,
    toothCount,
    toothStyle
  ) {
    const products = this.getProductsByProductGroupId(
      productGroupId,
      isDealerApp,
      productIdToForceInclude
    );

    if (thumbBucketProfileId) {
      if (toothCount && toothStyle) {
        return products.filter(
          (x) =>
            x.thumbProductDetailBucketProfiles &&
            x.thumbProductDetailBucketProfiles.some(
              (y) =>
                y.thumbBucketProfileId === thumbBucketProfileId &&
                y.toothCount === parseInt(toothCount) &&
                y.toothStyle === toothStyle
            )
        );
      } else {
        return products.filter(
          (x) =>
            x.thumbProductDetailBucketProfiles &&
            x.thumbProductDetailBucketProfiles.some(
              (y) => y.thumbBucketProfileId === thumbBucketProfileId
            )
        );
      }
    } else {
      return products.filter( x => !x.thumbHideWhenNoBucketSpecs);
    }
  }

  getProductOptionSetsByProductGroupId(productGroupId) {
    const productGroup = this.productGroups.find(
      (x) => x.id === productGroupId
    );
    if (!productGroup) return [];
    return productGroup.productOptionSets;
  }

  getProductOption(productGroupId, optionSetId, optionId, allowItemToNotExist) {
    let option = null;
    const productGroup = this.productGroups.find(
      (x) => x.id === productGroupId
    );
    if (productGroup) {
      var optionSet = productGroup.productOptionSets.find(
        (x) => x.id === optionSetId
      );
      if (optionSet) {
        optionSet.productOptionsBySeries.forEach((optionGroup) => {
          const optionToAssign = optionGroup.productOptions.find(
            (x) => x.id === optionId
          );
          if (optionToAssign) {
            option = optionToAssign;
          }
        });
      }
    }
    if (option) {
      return option;
    }
    if (!allowItemToNotExist) {
      throw Error(
        `Could not find product option ${optionId} given product group ${productGroupId} and product option set ${optionSetId}`
      );
    }
    return undefined;
  }

  isSingleAttachmentPointCarrier() {
    return this.attachmentPoints.length === 1;
  }

  _parseCouplerStyles(jsonList) {
    return jsonList.map((x) => new QuoteCouplerStyle(x));
  }

  getAllInventoryItems() {
    return this.inventoryItems;
  }

  getInventoryItemById(id) {
    return this.inventoryItems.find(x=> x.id === id);
  }
}
